import {
  breakpointSizes,
  getBreakpointDown,
  getBreakpointOnly,
  getBreakpointUp,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React from 'react'
import CollapsibleMiddleBox from 'src/components/CollapsibleMiddleBox/CollapsibleMiddleBox'
import EmptyLabel from 'src/components/EmptyLabel/EmptyLabel'
import RecurrenceFrequencyLabel from 'src/components/RecurrenceFrequencyLabel/RecurrenceFrequencyLabel'
import RecurringTooltip from 'src/components/RecurringTooltip/RecurringTooltip'
import { ExtendCurrency } from 'src/enums'
import Crncy from 'src/localization/Crncy'
import { useTranslate } from 'src/localization/Trns'
import { RecurrenceModel } from 'src/models/recurrence.models'
import { css } from 'styled-components'

interface RecurringBoxProps {
  className?: string
  timezone: string
  isInactive: boolean
  recurrence: RecurrenceModel
  isBudget?: boolean
  openLeftMdUp: boolean
  openLeftSmDown: boolean
  currency: ExtendCurrency
}

const InfoStack: React.FC<{
  header: React.ReactNode
  body: React.ReactElement | string
  isTop?: boolean
  isBottom?: boolean
}> = (props): React.ReactElement => {
  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;

        ${getBreakpointOnly(breakpointSizes.XXSM)(css`
          margin-top: ${styleConstants.heights.cardPadding};

          &:last-child {
            margin-bottom: ${styleConstants.heights.cardPadding};
          }
        `)}

        ${getBreakpointUp(breakpointSizes.XSM)(css`
          ${!!props.isTop &&
          css`
            margin-top: ${styleConstants.heights.cardPadding};
          `}
          ${!!props.isBottom &&
          css`
            margin-bottom: ${styleConstants.heights.cardPadding};
          `}
        `)}
      `}
    >
      <span
        css={css`
          ${getFontCss(
            14,
            styleConstants.colors.gray_darkest,
            styleConstants.fontFamilies.regular,
          )};
        `}
      >
        {props.header}
      </span>
      <span
        css={css`
          margin-top: 6px;
          ${getFontCss(
            16,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.regular,
          )};
        `}
      >
        {props.body}
      </span>
    </div>
  )
}

const RecurringBox: React.FC<RecurringBoxProps> = (
  props,
): React.ReactElement => {
  const translate = useTranslate()

  const nextRecurrenceAtTimezonedString =
    props.recurrence.nextRecurrenceAtTimezonedString(props.timezone)

  const topContent = (
    <div
      css={css`
        display: flex;
        justify-content: space-between;
        align-items: center;

        padding: ${styleConstants.heights.cardPadding}
          ${styleConstants.widths.cardPaddingXSMUp};
        ${getBreakpointOnly(breakpointSizes.XXSM)(css`
          padding: ${styleConstants.heights.cardPadding}
            ${styleConstants.widths.cardPaddingXXSMOnly};
        `)}
      `}
    >
      <div
        css={css`
          display: flex;
        `}
      >
        <span
          css={css`
            ${getFontCss(
              20,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.medium,
            )};
            margin-right: 12px;
            white-space: nowrap;
          `}
        >
          {translate('Recurring')}
        </span>
        {nextRecurrenceAtTimezonedString !== undefined && !props.isInactive && (
          <span
            css={css`
              ${getFontCss(
                20,
                themeConstants.colors.highlight,
                styleConstants.fontFamilies.regular,
              )};
            `}
          >
            {translate('${recurringAmount} on ${nextResetDate}', {
              recurringAmount: (
                <Crncy
                  currency={props.currency}
                  amount={props.recurrence.balance}
                />
              ),
              nextResetDate: nextRecurrenceAtTimezonedString,
            })}
          </span>
        )}
        {(nextRecurrenceAtTimezonedString === undefined ||
          props.isInactive) && (
          <span
            css={css`
              ${getFontCss(
                20,
                styleConstants.colors.red,
                styleConstants.fontFamilies.regular,
              )};
            `}
          >
            {translate('No longer recurring')}
          </span>
        )}
      </div>
      <RecurringTooltip
        css={css`
          ${getBreakpointDown(breakpointSizes.SM)(css`
            display: none;
          `)};
        `}
        openLeft={props.openLeftMdUp}
        isBudget={props.isBudget}
      />
      <RecurringTooltip
        css={css`
          ${getBreakpointUp(breakpointSizes.MD)(css`
            display: none;
          `)};
        `}
        openLeft={props.openLeftSmDown}
        isBudget={props.isBudget}
      />
    </div>
  )

  const middleContent = (
    <div
      css={css`
        padding: 0px ${styleConstants.widths.cardPaddingXSMUp};

        ${getBreakpointDown(breakpointSizes.XXSM)(css`
          padding: 0px ${styleConstants.widths.cardPaddingXXSMOnly};
        `)}

        ${getBreakpointUp(breakpointSizes.XSM)(css`
          display: grid;
          grid-template-columns: 1fr 1fr;
          grid-template-rows: 1fr 1fr;
          column-gap: 40px;
          row-gap: 40px;
        `)}

      ${getBreakpointOnly(breakpointSizes.XXSM)(css`
          display: flex;
          flex-direction: column;
        `)}
      `}
    >
      <InfoStack
        isTop
        header={translate('Frequency')}
        body={
          <RecurrenceFrequencyLabel
            recurrence={props.recurrence}
            timezone={props.timezone}
          />
        }
      />
      <InfoStack
        isTop
        header={translate('Next Reset')}
        body={
          props.recurrence.nextRecurrenceAtTimezonedString(props.timezone) !==
          undefined ? (
            <>
              {props.recurrence.nextRecurrenceAtTimezonedString(props.timezone)}
            </>
          ) : (
            <EmptyLabel />
          )
        }
      />
      <InfoStack
        isBottom
        header={translate('Last Reset')}
        body={
          props.recurrence.prevRecurrenceAtTimezonedString(props.timezone) !==
          undefined ? (
            <>
              {props.recurrence.prevRecurrenceAtTimezonedString(props.timezone)}
            </>
          ) : (
            <EmptyLabel />
          )
        }
      />
      {props.recurrence.remainingCount !== undefined && (
        <InfoStack
          isBottom
          header={translate('Total Remaining Resets')}
          body={
            <>
              {props.recurrence.remainingCount > 0 &&
                translate('${amount} after ${remainingCount} resets', {
                  amount: (
                    <Crncy
                      currency={props.currency}
                      amount={
                        props.recurrence.balance *
                        props.recurrence.remainingCount
                      }
                    />
                  ),
                  remainingCount: `${props.recurrence.remainingCount}`,
                })}
              {props.recurrence.remainingCount === 0 &&
                translate('No longer recurring')}
            </>
          }
        />
      )}
    </div>
  )

  const closedBottomLabel = translate('Details')
  const openBottomLabel = translate('Hide Details')

  return (
    <CollapsibleMiddleBox
      className={props.className}
      topContent={topContent}
      middleContent={middleContent}
      bottomContent={{
        closedBottomLabel: closedBottomLabel,
        openBottomLabel: openBottomLabel,
      }}
    />
  )
}

export default RecurringBox
